import React, { useEffect, useState } from 'react';
import { 
    Box, 
    Card, 
    CardBody, 
    CardHeader, 
    Heading, 
    Spinner, 
    Center, 
    Text 
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { DriverForm } from '@/components/forms/DriverForm';
import { apiClient } from '@/lib/api/client';
import { useToastNotification } from '@/lib/utils/useToastNotification';

interface Driver {
    id: number;
    first_name: string;
    last_name: string;
    contact_number: string;
    assigned_truck_plate_numbers: string[];
    helper_id: number | null;
    is_active: number;
}

const EditDriverPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showError } = useToastNotification();
    
    const [driver, setDriver] = useState<Driver | null>(null);
    const [loading, setLoading] = useState(true);

    const handleBack = () => navigate('/fleet-management/drivers');

    useEffect(() => {
        const fetchDriver = async () => {
            try {
                const res = await apiClient.get(`/drivers/${id}`);
                const rawData = res.data.data || res.data;

                // 🌟 SANITIZE DATA FOR THE FORM
                const sanitizedData = {
                    ...rawData,
                    // Remove all spaces from "+63 932..." so it becomes "+63932..." 
                    // This satisfies your form's regex: /^\+63\d{10}$/
                    contact_number: rawData.contact_number ? rawData.contact_number.replace(/\s+/g, '') : '',
                    
                    // Ensure helper_id is passed correctly
                    helper_id: rawData.helper_id || null,
                };

                setDriver(sanitizedData);
            } catch (err) {
                showError(err);
            } finally {
                setLoading(false);
            }
        };
        fetchDriver();
    }, [id, showError]);

    return (
        <AdminLayout>
            {/* 1. Container: Full viewport height, hidden overflow, flex layout */}
            <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
                
                {/* 2. Card: Fills container and splits into Header/Body */}
                <Card h="full" display="flex" flexDirection="column" variant="outline" boxShadow="sm">
                    
                    {/* 3. Header: Fixed at the top, no shrinking */}
                    <CardHeader py={4} borderBottom="1px" borderColor="gray.100" flexShrink={0}>
                        <Heading size="md">
                            Edit Driver: {driver ? `${driver.first_name} ${driver.last_name}` : 'Loading...'}
                        </Heading>
                        <Text fontSize="sm" color="gray.500">
                            Update profile and truck assignment details.
                        </Text>
                    </CardHeader>
                    
                    {/* 4. Body: Scrollable area for the form */}
                    <CardBody py={6} overflowY="auto" flex="1" minH={0}>
                        {loading ? (
                            <Center h="200px">
                                <Spinner color="blue.500" size="xl" thickness="4px" />
                            </Center>
                        ) : (
                            <DriverForm 
                                key={driver?.id} // Forces fresh mount when data is fetched
                                driverId={Number(id)} 
                                initialData={driver} 
                                onSuccess={handleBack}
                                onCancel={handleBack}
                            />
                        )}
                    </CardBody>
                </Card>
            </Box>
        </AdminLayout>
    );
};

export default EditDriverPage;
